/**
 * Design tokens for the Job Bid Quality surfaces (submission form + the
 * admin submissions review). Kept deliberately in the same shape as
 * pages/Claims/claimTokens.js, and pinned to the values the dashboard cards
 * already use, so Bid Quality reads as part of GMB rather than an embedded
 * third-party form.
 */
export const T = {
  surface: "#FFFFFF",
  subtle: "#FBFCFE",
  field: "#F6F7F9",
  line: "#EAECF0",
  hairline: "#EEF1F4",
  fieldLine: "#E4E7EC",
  ink: "#0B1320",
  ink2: "#344054",
  muted: "#667085",
  faint: "#98A2B3",
  good: "#12A150",
  bad: "#D92D20",
  warn: "#B54708",
};

export const FONT = '"DM Sans", "Inter", sans-serif';

/**
 * The house card frame — 1.5px #EAECF0 hairline, 12px radius, barely-there
 * shadow. Same frame as the Gate cards / stat tiles on the dashboards.
 */
export const CARD_SX = {
  fontFamily: FONT,
  backgroundColor: T.surface,
  border: `1.5px solid ${T.line}`,
  borderRadius: "12px",
  boxShadow: "0px 1px 2px rgba(16,24,40,0.04)",
};

/** Section title inside a card: 13px/600, the ink colour, tight tracking. */
export const CARD_TITLE_SX = {
  fontFamily: FONT,
  fontSize: "13px",
  fontWeight: 600,
  color: T.ink,
  letterSpacing: "-0.01em",
};

/** Micro-label above a field / under a figure: 12px/500 muted. */
export const MICRO_LABEL_SX = {
  fontFamily: FONT,
  fontSize: "12px",
  fontWeight: 500,
  color: T.muted,
  letterSpacing: 0,
};

/** Soft status fill / border pair — 10% fill, 40% border, as the Gates use. */
export const softTone = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return {
    ink: hex,
    fill: `rgba(${r}, ${g}, ${b}, 0.1)`,
    border: `rgba(${r}, ${g}, ${b}, 0.4)`,
  };
};

export const TONE = {
  good: softTone(T.good),
  bad: softTone(T.bad),
  warn: softTone(T.warn),
  neutral: { ink: T.faint, fill: T.hairline, border: T.line },
};

/**
 * How a 0-100 job score is described and coloured. The bands mirror how the
 * Bid Accuracy metric is read on the dashboards: a bid is either clean (100),
 * close (80+), shaky (60+) or a miss.
 * @param {number|string|null|undefined} score
 */
export const scoreBand = (score) => {
  const n = Number(score);
  if (!Number.isFinite(n)) return { label: "No score", ...TONE.neutral };
  if (n >= 100) return { label: "Perfect bid", ...TONE.good };
  if (n >= 80) return { label: "Strong bid", ...TONE.good };
  if (n >= 60) return { label: "Mixed bid", ...TONE.warn };
  return { label: "Needs work", ...TONE.bad };
};

/**
 * Score figures are compared down a column on the reviews table and counted up
 * live on the form, so they use tabular figures — otherwise the digits change
 * width as the number changes and the layout twitches.
 */
export const FIGURE_SX = {
  fontFamily: FONT,
  fontVariantNumeric: "tabular-nums",
  fontFeatureSettings: '"tnum"',
  letterSpacing: "-0.02em",
};
